import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Check, X, AlertOctagon } from 'lucide-react';
import { setSkipConfirm, getSkipConfirm } from '../hooks/useAIChat';

/**
 * Inline confirmation card rendered inside a chat assistant message
 * when the AI parsed a write-mode intent.
 */
export const AIActionCard = ({ message, msgIndex, onConfirm, onCancel }) => {
  const { content, action, matches = [], autoSelectId, confirming, confirmed, cancelled } = message;
  const [selectedId, setSelectedId] = useState(autoSelectId || matches[0]?.id || '');
  const [skip, setSkip] = useState(getSkipConfirm());

  const done = confirmed || cancelled;

  return (
    <div
      data-testid="ai-action-card"
      className="rounded-2xl p-4 space-y-3"
      style={{
        background: done
          ? (confirmed ? 'rgba(34,197,94,0.08)' : 'rgba(248,113,113,0.06)')
          : 'rgba(99,102,241,0.10)',
        border: done
          ? (confirmed ? '1px solid rgba(34,197,94,0.3)' : '1px solid rgba(248,113,113,0.25)')
          : '1px solid rgba(99,102,241,0.25)',
      }}
    >
      <div className="flex items-start gap-2.5">
        <AlertOctagon size={18} style={{ color: '#A5B4FC' }} className="shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: '#A5B4FC' }}>
            Confirm update
          </p>
          <div className="text-sm text-white mt-1 ai-markdown">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        </div>
      </div>

      {matches.length > 1 && !done && (
        <div className="space-y-1.5">
          <p className="text-[11px]" style={{ color: '#94A3B8' }}>Multiple matches — pick one:</p>
          {matches.map((m) => (
            <label
              key={m.id}
              data-testid="ai-match-option"
              className="flex items-start gap-2 text-xs cursor-pointer p-2 rounded-lg transition-colors"
              style={{
                background: selectedId === m.id ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.03)',
                border: selectedId === m.id ? '1px solid rgba(99,102,241,0.4)' : '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <input
                type="radio"
                checked={selectedId === m.id}
                onChange={() => setSelectedId(m.id)}
                className="mt-0.5"
              />
              <span style={{ color: '#CBD5E1' }}>{m.summary}</span>
            </label>
          ))}
        </div>
      )}
      {matches.length === 1 && !done && (
        <p className="text-xs" style={{ color: '#94A3B8' }}>Target: <span className="text-white">{matches[0].summary}</span></p>
      )}

      {!done && (
        <>
          <label className="flex items-center gap-2 text-[11px] cursor-pointer pt-1" style={{ color: '#94A3B8' }}>
            <input
              type="checkbox"
              data-testid="ai-skip-confirm-toggle"
              checked={skip}
              onChange={(e) => { setSkip(e.target.checked); setSkipConfirm(e.target.checked); }}
            />
            Don&apos;t ask me again this session (only auto-applies clear single matches)
          </label>

          <div className="flex items-center gap-2 pt-1">
            <button
              data-testid="ai-action-confirm-btn"
              onClick={() => onConfirm(msgIndex, selectedId)}
              disabled={confirming || !selectedId}
              className="flex-1 py-2 text-xs font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #22C55E, #10B981)', color: '#fff' }}
            >
              <Check size={14} /> {confirming ? 'Applying…' : 'Confirm & update'}
            </button>
            <button
              data-testid="ai-action-cancel-btn"
              onClick={() => onCancel(msgIndex)}
              disabled={confirming}
              className="px-3 py-2 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition-colors"
              style={{ background: 'rgba(255,255,255,0.06)', color: '#CBD5E1', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <X size={14} /> Cancel
            </button>
          </div>
        </>
      )}

      {done && (
        <p className="text-xs" style={{ color: confirmed ? '#86EFAC' : '#FCA5A5' }}>
          {confirmed ? 'Update applied.' : 'Cancelled.'}
        </p>
      )}
    </div>
  );
};

export default AIActionCard;
